import { SessionData, SessionKeys, AppMap, App, OrganizationData, SubscriptionData, UserData } from './types';
import { convertProductLinesArrayToMap } from '../flags/product-lines.js';
import { deepFreeze } from '@/utils/deep-freeze.js';
import {
    hasGlobalAccess,
    hasAdminAccess,
    isAdmin,
    isV2SubAdmin,
    isAdoptManager,
    isGuest,
    isOps,
    isReadOnly,
    isSuper,
    isSupport,
    isUser,
} from '@/stores/auth.helpers';
import cloneDeep from 'lodash/cloneDeep';

/* Notes:
- This data is set on load and is NON-REACTIVE to changes in app state. Do not source from
  here if you expect the data to change (such as on admin routes).
- Vue components read this via $activeUser (and related globals), which delegate to getKey()
  on each access. Computed properties that cache $activeUser will not refresh when session
  data is replaced unless something else triggers a re-render.
*/
let localSessionData: SessionData | undefined;
let mostRecentClearError: Error | null;

/* Getter/setters */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function setGlobalSessionData (update: Record<string, any>): void {
    if (!update) {
        return;
    }

    try {
        // Assumes the data is provided by api/init on load;
        // we do not want to block this action on any other call
        localSessionData = deepFreeze(convertObjectToSessionData(cloneDeep(update)));
        mostRecentClearError = null;
    } catch {
        gracefullyFail('Update for session data is in the wrong format.');
    }
}

export function getActiveUser (): UserData | undefined {
    return getKey('activeUser');
}

export function getActiveSubscription (): SubscriptionData | undefined {
    return getKey('activeSubscription');
}

export function getActiveApplicationsMap (): AppMap {
    return getKey('activeApplications') || {};
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getKey (key: SessionKeys): any {
    const sessionData = ensureSessionData();

    return sessionData[key];
}

export function clearGlobalSessionData (error: Error): void {
    localSessionData = undefined;
    mostRecentClearError = error;
}

export function updateActiveUserProfile (
    profile: Partial<Pick<UserData, 'firstName' | 'lastName' | 'fullName' | 'email'>>,
): void {
    if (!localSessionData?.activeUser) {
        return;
    }

    localSessionData = deepFreeze({
        ...cloneDeep(localSessionData),
        activeUser: {
            ...cloneDeep(localSessionData.activeUser),
            ...profile,
        },
    });
}

function ensureSessionData (): SessionData {
    if (!localSessionData) {
        if(mostRecentClearError) {
            return gracefullyFail(`Session was cleared for: ${mostRecentClearError}`);
        }

        return gracefullyFail('Session data has not been set up.');

    }

    return localSessionData;
}

function gracefullyFail (error: unknown): SessionData {
    // eslint-disable-next-line no-console
    console.warn(`Something went wrong with session data retrieval: ${error}`);

    return {};
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function convertObjectToSessionData (update: Record<string, any>): SessionData {
    const { activeOrganization }: { activeOrganization?: OrganizationData } = update;

    const sessionData: SessionData = {
        activeOrganization,
        activeSubscription: configureSubscriptionData(update.activeSubscription),
        activeApplications: convertApplicationsToMap(update.activeSubscription.applications),
        activeUser: configureUserData(update.user),
    };

    return sessionData;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function configureSubscriptionData (sub: Record<string, any>): SubscriptionData {
    const {
        value,
        id,
        name,
        displayName = 'Unknown Sub',
        productLines = [],
        featureFlags = {},
        timeZone,
        locale,
        firstEventTime,
        autoGrantAccessExpiresOn = 0,
    } = sub;

    const activeSubscription: SubscriptionData = {
        id: value || id,
        name,
        displayName,
        timeZone,
        locale,
        firstEventTime,
        productLines: convertProductLinesArrayToMap(productLines),
        featureFlags,
        autoGrantAccessExpiresOn,
    };

    return activeSubscription;
}

function convertApplicationsToMap (applications = []): AppMap {
    return applications.reduce((acc: AppMap, app: App)=> {
        acc[app.id] = app;

        return acc;
    }, {});
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function configureUserData (user: Record<string, any>): UserData {
    const { id, username, flags, userflags, isPendozer, locale, email } = user;
    const firstName = user.firstName ?? user.first;
    const lastName = user.lastName ?? user.last;
    const fullName = user.fullName
        || [firstName, lastName].filter(Boolean).join(' ')
        || undefined;

    return {
        id,
        username,
        fullName,
        firstName,
        lastName,
        email,
        locale,
        flags,
        userflags,
        isPendozer,
        hasAdminAccess: hasAdminAccess(user),
        isAdmin: isAdmin(user),
        isV2SubAdmin: isV2SubAdmin(user),
        isSuper: isSuper(user),
        isSupport: isSupport(user),
        isOps: isOps(user),
        isGuest: isGuest(user),
        isUser: isUser(user),
        isReadOnly: isReadOnly(user),
        isAdoptManager: isAdoptManager(user),
        hasGlobalAccess: hasGlobalAccess(user),
    };
}
